import React, { memo, useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  Tooltip,
  Grid,
  FormControl,
  InputLabel,
  NativeSelect,
  TextField,
  InputAdornment,
  Typography,
  Stack,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Swal from "sweetalert2";
import configServ from "../../../services/config";
import { useSelector } from "react-redux";
import useDeviceDetect from "../../../Components/Detect/useDeviceDetect ";
import LoaderNew from "../../Loader/LoaderNew";

//FUNCTION
const RejectionReasonsTable = ({ isChanged, setIsChanged, handleEdit, pagination = true }) => {
  //#region code
  const { is_super, is_admin } = useSelector(
    (state) => state.GlobalVariables
  );
  const rowsPerPageOptions = [20, 50, 100, 200];
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[1]);
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // "" | "1" | "0"
  const { isMobile } = useDeviceDetect();

  const canEdit = Boolean(is_super || is_admin);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const fetchList = async () => {
    try {
      const dataToSend = {
        search_by: "reasons",
        search: search,
        per_page: rowsPerPage,
        page: page,
      };
      if (statusFilter !== "") {
        dataToSend.status = Number(statusFilter);
      }
      const res = await configServ.getRejectionReasons(dataToSend);
      if (res.status === 200) {
        setList(res.data || []);
        setCount(res.total || 0);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, [isChanged, page, rowsPerPage, search, statusFilter]);

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: "Delete this rejection reason?",
      text: `"${row.reasons}" will be permanently removed.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    try {
      const res = await configServ.deleteRejectionReason({ id: row.id });
      if (res.status === 200) {
        Swal.fire({
          title: "Deleted",
          text: res.message || "Rejection reason deleted successfully.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
        setIsChanged(!isChanged);
      } else if (res.status === 404) {
        Swal.fire("Not found", res.message || "Rejection reason not found.", "info");
      } else {
        Swal.fire("Error", res.message || "Failed to delete.", "error");
      }
    } catch (e) {
      console.log(e);
      Swal.fire("Error", "An unexpected error occurred.", "error");
    }
  };

  const headers = ["REASON", "STATUS", "ACTION"];

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, count - page * rowsPerPage);

  const headerCellStyle = {
    fontSize: 14,
    fontWeight: "bold",
    backgroundColor: "#4CB5F5",
    color: "white",
  };
  const cellStyle = { py: 0, my: 0 };
  const evenRowStyle = { background: "#f5f5f5" };
  const oddRowStyle = { background: "white" };
  const rowStyle = { "&:hover": { background: "#f1f9fe" } };

  const handleStatusFilter = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleSearch = (event) => {
    const data = event.target.value;
    setSearch(data === "" ? null : data);
    setPage(0);
  };
  //#endregion

  if (loading) {
    return (
      <Box
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <LoaderNew />
      </Box>
    );
  }

  return (
    <>
      {/* search */}
      <Grid
        container
        alignItems="flex-end"
        justifyContent="flex-start"
        sx={{ pt: 1 }}
        spacing={1}
        direction={"row"}
      >
        <Grid item xs={12} md={6} ml={0} justifyContent={"flex-start"}>
          <Typography sx={{ fontSize: "18px", fontWeight: "bold" }}>
            Rejection Reasons: {count}
          </Typography>
        </Grid>
        <Grid item xs={12} md={3}>
          <Box sx={{ minWidth: 120 }}>
            <FormControl fullWidth>
              <InputLabel variant="standard" htmlFor="status-filter">
                
              </InputLabel>
              <NativeSelect
                value={statusFilter}
                onChange={handleStatusFilter}
                inputProps={{ name: "status", id: "status-filter" }}
              >
                <option value={""}>All</option>
                <option value={"1"}>Active</option>
                <option value={"0"}>Inactive</option>
              </NativeSelect>
            </FormControl>
          </Box>
        </Grid>
        <Grid item xs={12} md={3} justifyContent={"flex-end"}>
          <TextField
            id="search"
            size="small"
            fullWidth
            placeholder="search by reason"
            variant="outlined"
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ fill: "grey" }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      {isMobile ? (
        <>
          <Grid container spacing={2}>
            {list.length > 0 ? (
              list.map((row, rowIndex) => (
                <Grid item xs={12} key={rowIndex}>
                  <Paper elevation={3} sx={{ p: 2 }}>
                    <Stack spacing={1}>
                      <Box sx={{ fontWeight: "bold" }}>
                        {page * rowsPerPage + rowIndex + 1}. {row.reasons}
                      </Box>
                      <Box>
                        Status:{" "}
                        {Number(row.status) === 1 ? (
                          <CheckCircleIcon color="success" />
                        ) : (
                          <CancelIcon color="error" />
                        )}
                      </Box>
                      <Box display={canEdit ? "block" : "none"}>
                        <Stack direction="row" spacing={1}>
                          <EditIcon
                            sx={{
                              borderRadius: 10,
                              cursor: "pointer",
                              p: 0.5,
                              ":hover": { backgroundColor: "#99999955" },
                            }}
                            color="primary"
                            onClick={() => {
                              handleEdit(row);
                              scrollToTop();
                            }}
                          />
                          <DeleteIcon
                            sx={{
                              borderRadius: 10,
                              cursor: "pointer",
                              p: 0.5,
                              ":hover": { backgroundColor: "#99999955" },
                            }}
                            color="error"
                            onClick={() => handleDelete(row)}
                          />
                        </Stack>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              ))
            ) : (
              <Grid item xs={12} textAlign="center">
                <Box sx={{ color: "red", fontWeight: 600 }}>No data found.</Box>
              </Grid>
            )}
          </Grid>
          {pagination && (
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={count}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </>
      ) : (
        <TableContainer component={Paper} sx={{ py: 1 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellStyle}>S.No.</TableCell>
                {headers.map((header, index) => (
                  <TableCell key={index} sx={headerCellStyle}>
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((row, rowIndex) => (
                <TableRow
                  key={rowIndex}
                  sx={{
                    ...rowStyle,
                    ...(rowIndex % 2 === 0 ? evenRowStyle : oddRowStyle),
                  }}
                >
                  <TableCell sx={cellStyle}>
                    {page * rowsPerPage + rowIndex + 1}
                  </TableCell>
                  <TableCell sx={cellStyle}>{row.reasons}</TableCell>
                  <TableCell sx={cellStyle}>
                    {Number(row.status) === 1 ? (
                      <CheckCircleIcon color="success" />
                    ) : (
                      <CancelIcon color="error" />
                    )}
                  </TableCell>
                  <TableCell sx={cellStyle}>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      display={canEdit ? "flex" : "none"}
                    >
                      <Box
                        sx={{
                          borderRadius: 10,
                          width: "fit-content",
                          cursor: "pointer",
                          p: 0.5,
                          ":hover": { backgroundColor: "#99999955" },
                        }}
                        onClick={() => {
                          handleEdit(row);
                          scrollToTop();
                        }}
                      >
                        <Tooltip title="Edit" arrow>
                          <EditIcon color="primary" />
                        </Tooltip>
                      </Box>
                      <Box
                        sx={{
                          borderRadius: 10,
                          width: "fit-content",
                          cursor: "pointer",
                          p: 0.5,
                          ":hover": { backgroundColor: "#99999955" },
                        }}
                        onClick={() => handleDelete(row)}
                      >
                        <Tooltip title="Delete" arrow>
                          <DeleteIcon color="error" />
                        </Tooltip>
                      </Box>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {emptyRows > 0 && (
                <TableRow style={{ height: 1 * emptyRows }}>
                  <TableCell colSpan={headers.length + 2} />
                </TableRow>
              )}
            </TableBody>
          </Table>
          {pagination && (
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={count}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </TableContainer>
      )}
    </>
  );
};

export default memo(RejectionReasonsTable);
